/**
 * v38.9 — Production Cleanup page (Super-Admin only).
 *
 * Used at go-live to wipe all transactional + demo data from the database
 * while keeping ONLY:
 *   - Hospital branding records
 *   - Super-Admin accounts
 *   - Hospital-Admin accounts
 *   - RBAC (roles + permissions) + subscription plans + partner API keys
 *   - Master config tables (consultation types, shift types, leave types, etc.)
 *
 * Wipes everything else, including: patients, appointments, invoices, beds,
 * wards, departments, tariffs (service_catalog), audit logs, all transactional
 * staff (doctors, nurses, reception, billing, etc.).
 *
 * The destructive POST requires the operator to type the exact hospital code
 * (e.g. "prashanti-bhubaneswar") into a confirmation box.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { Navigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { AlertTriangle, Database, Loader2, ShieldX, Trash2 } from 'lucide-react';

const ProductionCleanupPage = () => {
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmCode, setConfirmCode] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/admin/cleanup-preview');
        setPreview(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load preview');
      } finally { setLoading(false); }
    };
    load();
  }, []);

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_super_admin) {
    return (
      <Card className="p-8 max-w-xl mx-auto" data-testid="cleanup-forbidden">
        <ShieldX className="h-10 w-10 text-rose-500 mb-3" />
        <h2 className="text-xl font-semibold mb-2">Forbidden</h2>
        <p className="text-sm text-slate-600">Only Super Administrators can access the Production Cleanup tool.</p>
      </Card>
    );
  }

  const runCleanup = async (dryRun) => {
    if (!confirmCode.trim()) {
      toast.error('Type the hospital code to confirm.');
      return;
    }
    if (!dryRun && !window.confirm(`This will PERMANENTLY DELETE all transactional and demo data.\n\nConfirmation code: "${confirmCode}"\n\nAre you absolutely sure?`)) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await api.post('/admin/production-cleanup', {
        confirm_hospital_code: confirmCode.trim(),
        dry_run: dryRun,
      });
      setResult(res.data);
      toast.success(dryRun
        ? `Dry run complete — ${res.data.users_deleted} users would be deleted`
        : `Cleanup complete — ${res.data.users_deleted} users, ${Object.values(res.data.wiped || {}).reduce((a, b) => a + b, 0)} transactional records deleted`);
      if (!dryRun) setConfirmCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const totalToWipe = (preview?.wipe || []).reduce((s, c) => s + c.count, 0);

  return (
    <div className="space-y-6" data-testid="production-cleanup-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Trash2 className="h-6 w-6 text-rose-600" />
          Production Cleanup
        </h1>
        <p className="text-sm text-slate-500 mt-1">Wipe demo/test data and prepare the database for live ingestion. <strong className="text-rose-600">This is destructive and irreversible.</strong></p>
      </div>

      <Alert variant="destructive" className="border-rose-200 bg-rose-50">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="text-rose-800 text-sm leading-relaxed">
          <strong>What gets DELETED:</strong> All patients, appointments, invoices, payments, prescriptions, lab reports, beds, wards, IPD admissions, diagnoses, vitals, attendance, leave records, audit logs, departments, tariffs (service catalog), procedures, files, and every non-admin staff account (doctors, nurses, billing, reception, HR, IT, etc.).
          <br /><br />
          <strong>What is KEPT:</strong> Hospital branding records, Super-Admin accounts ({preview?.users_surviving} surviving), Hospital-Admin accounts, RBAC (roles + permissions), Subscription plans, Partner API keys, and all master config tables.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 border-emerald-200 bg-emerald-50/40">
          <p className="text-xs uppercase text-emerald-700 font-semibold mb-1">Will Keep</p>
          <p className="text-3xl font-bold text-emerald-700">{preview?.keep?.length || 0}</p>
          <p className="text-xs text-emerald-600 mt-1">collections (config/master)</p>
        </Card>
        <Card className="p-5 border-rose-200 bg-rose-50/40">
          <p className="text-xs uppercase text-rose-700 font-semibold mb-1">Will Wipe</p>
          <p className="text-3xl font-bold text-rose-700">{preview?.wipe?.length || 0}</p>
          <p className="text-xs text-rose-600 mt-1">collections · {totalToWipe.toLocaleString()} records</p>
        </Card>
        <Card className="p-5 border-amber-200 bg-amber-50/40">
          <p className="text-xs uppercase text-amber-700 font-semibold mb-1">Surviving Users</p>
          <p className="text-3xl font-bold text-amber-700">{preview?.users_surviving} <span className="text-base text-amber-500">/ {preview?.users_total}</span></p>
          <p className="text-xs text-amber-600 mt-1">super-admin + hospital-admin only</p>
        </Card>
      </div>

      <Card className="p-6 border-2 border-rose-300 bg-white">
        <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Database className="h-5 w-5 text-rose-600" />
          Confirmation Required
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          Type the <strong>exact</strong> hospital code (e.g. <code className="px-1.5 py-0.5 rounded bg-slate-100 text-rose-700 font-mono text-xs">prashanti-bhubaneswar</code>) to confirm you understand the destructive nature of this action.
        </p>
        <Label htmlFor="confirm" className="text-sm font-medium">Hospital Code</Label>
        <Input
          id="confirm"
          value={confirmCode}
          onChange={(e) => setConfirmCode(e.target.value)}
          placeholder="e.g., prashanti-bhubaneswar"
          className="font-mono mt-1 mb-4"
          data-testid="cleanup-confirm-input"
        />
        <div className="flex gap-3 flex-wrap">
          <Button
            variant="outline"
            onClick={() => runCleanup(true)}
            disabled={running || !confirmCode.trim()}
            data-testid="cleanup-dry-run-btn"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Run Dry-Run (preview only, no writes)
          </Button>
          <Button
            variant="destructive"
            onClick={() => runCleanup(false)}
            disabled={running || !confirmCode.trim()}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="cleanup-execute-btn"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Execute Cleanup (PERMANENT)
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-6 border-emerald-200 bg-emerald-50/40" data-testid="cleanup-result">
          <h3 className="text-base font-semibold text-emerald-800 mb-3">
            {result.dry_run ? 'Dry-run Result' : '✓ Cleanup Executed'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-slate-700 mb-4">
            <div><span className="text-slate-500">Started</span><br /><span className="font-mono">{result.started_at?.slice(11, 19)}</span></div>
            <div><span className="text-slate-500">Users kept</span><br /><span className="font-mono">{result.users_kept}</span></div>
            <div><span className="text-slate-500">Users deleted</span><br /><span className="font-mono">{result.users_deleted}</span></div>
            <div><span className="text-slate-500">Staff profiles deleted</span><br /><span className="font-mono">{result.staff_profiles_deleted}</span></div>
          </div>
          <details>
            <summary className="text-sm font-medium cursor-pointer text-emerald-700">Per-collection delete counts ({Object.keys(result.wiped || {}).length})</summary>
            <div className="mt-3 max-h-72 overflow-y-auto grid grid-cols-2 gap-1 text-xs font-mono">
              {Object.entries(result.wiped || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-emerald-100 py-1">
                  <span className="text-slate-600">{k}</span>
                  <span className="text-rose-600 font-semibold">{v}</span>
                </div>
              ))}
            </div>
          </details>
        </Card>
      )}
    </div>
  );
};

export default ProductionCleanupPage;
